/**
 * w0.2 — Boot-time tenant brand loader (admin-app).
 *
 * Mirrors the customer-app loader. Kept duplicated (not extracted to
 * @ferro/ui or similar) for the same reasons `brand.ts` is duplicated:
 *   - the function touches global DOM state via `installBrandStyles`
 *   - test environments (jsdom) bind storage and window per-app
 *   - the deferred package export from `@ferro/config` would force a
 *     fetch-vs-no-fetch boundary into a config package that's
 *     otherwise schema-only
 *
 * Behavior matches the customer-app version exactly:
 *   - 1.5s timeout per attempt; transient failures (network/timeout/5xx)
 *     retry with short backoff (#241), then fall through to static config
 *   - 5-minute localStorage cache keyed by slug
 *   - never throws — always returns a usable ClientConfig
 *
 * SECURITY NOTE: brand is public-facing visual data. The same staff
 * accessing /admin will be looking at a brand they administer; a 5-min
 * stale cache after a brand-kit publish is acceptable.
 *
 * IMPORTANT: this file is the W0.2 sibling of `lib/api.ts` — the API
 * client expansion (W0.5) owns api.ts. This module ONLY uses native
 * `fetch` so it doesn't pull in api.ts's typed namespaces or share
 * its retry / 401 semantics.
 */

import type { Brand, ClientConfig } from '@ferro/config';

const CACHE_KEY_PREFIX = 'ferro:tenant-brand:';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const FETCH_TIMEOUT_MS = 1500; // 1.5s — boot must not block longer

interface CachedBrand {
  brand: Brand;
  version: number;
  cachedAt: number;
  source: 'db' | 'static';
}

interface BrandResponse {
  ok: boolean;
  source: 'db' | 'static';
  brand: Brand;
  version: number;
  updatedAt: string;
}

function cacheKey(slug: string): string {
  return `${CACHE_KEY_PREFIX}${slug}`;
}

function readCache(slug: string): CachedBrand | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(cacheKey(slug));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedBrand;
    if (typeof parsed.cachedAt !== 'number') return null;
    const age = Date.now() - parsed.cachedAt;
    if (age > CACHE_TTL_MS) return null;
    if (!parsed.brand || typeof parsed.brand.name !== 'string') return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(slug: string, payload: CachedBrand): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(cacheKey(slug), JSON.stringify(payload));
  } catch {
    // Storage full or disabled — drop the cache write silently.
  }
}

/**
 * Field-by-field merge so empty override fields fall back to base.
 */
export function mergeBrand(base: Brand, override: Brand): Brand {
  return {
    name: override.name || base.name,
    tagline: override.tagline ?? base.tagline,
    colors: {
      primary: override.colors.primary || base.colors.primary,
      secondary: override.colors.secondary || base.colors.secondary,
      accent: override.colors.accent || base.colors.accent,
      ...(override.colors.text || base.colors.text
        ? { text: override.colors.text ?? base.colors.text }
        : {}),
      ...(override.colors.background || base.colors.background
        ? { background: override.colors.background ?? base.colors.background }
        : {}),
    },
    fonts: {
      heading: override.fonts.heading || base.fonts.heading,
      body: override.fonts.body || base.fonts.body,
    },
    logo: override.logo || base.logo,
    ...(override.logoDark || base.logoDark
      ? { logoDark: override.logoDark ?? base.logoDark }
      : {}),
  };
}

export function applyBrandOverride(
  config: ClientConfig,
  override: Brand,
): ClientConfig {
  return {
    ...config,
    brand: mergeBrand(config.brand, override),
  };
}

// #241 — transient brand-fetch failures (network throw, timeout, 5xx —
// Railway cold-start 502s, cold-Neon 500s) retry with a short backoff
// before falling back to the static brand. 4xx responses and malformed
// bodies are definitive — no retry. Kept shorter than pickTenantConfig's
// schedule because the static tenant brand is already the CORRECT
// tenant; this overlay is incremental polish, not identity.
const RETRY_DELAYS_MS: readonly number[] = [250, 750];

export interface LoadTenantBrandOptions {
  fetchFn?: typeof fetch;
  timeoutMs?: number;
  bypassCache?: boolean;
  /** #241 — backoff between retries on transient failures. Pass [] to disable. */
  retryDelaysMs?: readonly number[];
  /** #241 — sleep seam for tests. */
  sleepFn?: (ms: number) => Promise<void>;
}

type FetchAttemptResult =
  | { kind: 'response'; res: Response }
  | { kind: 'threw'; err: unknown };

const defaultSleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

export async function loadTenantBrand(
  config: ClientConfig,
  opts: LoadTenantBrandOptions = {},
): Promise<ClientConfig> {
  const slug = config.id;
  const fetchFn = opts.fetchFn ?? globalThis.fetch;
  const timeoutMs = opts.timeoutMs ?? FETCH_TIMEOUT_MS;
  const retryDelays = opts.retryDelaysMs ?? RETRY_DELAYS_MS;
  const sleep = opts.sleepFn ?? defaultSleep;

  if (!opts.bypassCache) {
    const cached = readCache(slug);
    if (cached) {
      // eslint-disable-next-line no-console
      console.info(
        `Tenant brand served from cache (source=${cached.source}, version=${cached.version})`,
      );
      return applyBrandOverride(config, cached.brand);
    }
  }

  if (typeof fetchFn !== 'function') {
    return config;
  }

  const url = `${config.deployment.apiUrl}/api/tenants/${slug}/brand`;

  const attemptFetch = async (): Promise<FetchAttemptResult> => {
    const controller =
      typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller
      ? setTimeout(() => controller.abort(), timeoutMs)
      : null;
    try {
      const res = await fetchFn(url, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        ...(controller ? { signal: controller.signal } : {}),
      });
      if (timer) clearTimeout(timer);
      return { kind: 'response', res };
    } catch (err) {
      if (timer) clearTimeout(timer);
      return { kind: 'threw', err };
    }
  };

  let attempt = 0;
  for (;;) {
    const result = await attemptFetch();
    const retryDelay =
      attempt < retryDelays.length ? retryDelays[attempt] : undefined;

    if (result.kind === 'threw') {
      // Network failure / timeout — transient, retry if budget remains.
      if (retryDelay !== undefined) {
        await sleep(retryDelay);
        attempt += 1;
        continue;
      }
      // eslint-disable-next-line no-console
      console.warn('Tenant brand fetch threw; using static config', result.err);
      return config;
    }

    const res = result.res;
    if (!res.ok) {
      // 5xx = transient (cold start) → retry; 4xx = definitive → fall back.
      if (res.status >= 500 && retryDelay !== undefined) {
        await sleep(retryDelay);
        attempt += 1;
        continue;
      }
      // eslint-disable-next-line no-console
      console.warn(
        `Tenant brand fetch failed (${res.status}); using static config`,
      );
      return config;
    }

    let data: Partial<BrandResponse>;
    try {
      data = (await res.json()) as Partial<BrandResponse>;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Tenant brand fetch threw; using static config', err);
      return config;
    }
    if (!data.ok || !data.brand || typeof data.version !== 'number') {
      // eslint-disable-next-line no-console
      console.warn('Tenant brand response shape invalid; using static config');
      return config;
    }

    if (!opts.bypassCache) {
      writeCache(slug, {
        brand: data.brand,
        version: data.version,
        cachedAt: Date.now(),
        source: data.source ?? 'static',
      });
    }

    // eslint-disable-next-line no-console
    console.info(
      `Tenant brand loaded from ${data.source} version ${data.version}`,
    );
    return applyBrandOverride(config, data.brand);
  }
}

export function clearTenantBrandCache(slug: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(cacheKey(slug));
  } catch {
    // ignore
  }
}
